import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentsIcon from '@mui/icons-material/Payments';
import { DashboardLayout } from '@/shared/layouts/DashboardLayout';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { DashboardSkeleton } from '@/components/ui/Skeleton';
import { QuickActionGrid } from '@/components/ui/QuickActionGrid';
import { PageSection } from '@/components/ui/PageSection';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { financeApi } from '@/api/finance.api';
import type { FinanceCfoOverview, FinanceHealthIssue } from '@/types/finance';
import { showApiError } from '@/utils/apiError';
import { FINANCE_WORKSPACES } from '@/modules/finance/config/financeWorkspaces';

const WORKSPACE_ACTIONS = [
  { to: FINANCE_WORKSPACES.receivables.path, label: 'Receivables', description: 'Invoices, payments, and collections' },
  { to: FINANCE_WORKSPACES.payables.path, label: 'Payables', description: 'Vendor bills, expenses, and payment runs' },
  { to: FINANCE_WORKSPACES.treasury.path, label: 'Treasury', description: 'Cash position and forecast' },
  { to: FINANCE_WORKSPACES.reports.path, label: 'Reports', description: 'P&L, balance sheet, and analytics' },
  { to: FINANCE_WORKSPACES.administration.path, label: 'Administration', description: 'Setup, close, health, and workflows' },
];

function formatMoney(value?: number) {
  if (value == null) return '—';
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function IssuePreview({ issues }: { issues: FinanceHealthIssue[] }) {
  if (issues.length === 0) {
    return <p className="text-sm text-slate-500">No open issues — your books look healthy.</p>;
  }
  return (
    <ul className="space-y-2">
      {issues.slice(0, 4).map((issue) => (
        <li key={`${issue.category}-${issue.title}`} className="rounded-lg border p-3 text-sm">
          <div className="font-medium">{issue.title}</div>
          <p className="text-slate-600 mt-1">{issue.description}</p>
          {issue.actionPath && issue.actionLabel && (
            <Link to={issue.actionPath} className="inline-block mt-2 text-primary-600 text-xs hover:underline">
              {issue.actionLabel} →
            </Link>
          )}
        </li>
      ))}
    </ul>
  );
}

export function FinanceDashboardPage() {
  const [overview, setOverview] = useState<FinanceCfoOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    financeApi
      .cfoOverview()
      .then(setOverview)
      .catch((err) => showApiError(err, 'Failed to load finance overview'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout
        moduleLabel="Finance"
        title="Finance overview"
        description="Executive view of cash, revenue, and business health — no accounting jargon"
      >
        <DashboardSkeleton kpiCount={8} />
      </DashboardLayout>
    );
  }

  const setupProgress =
    overview && overview.setupStepsTotal > 0
      ? `${overview.setupStepsCompleted}/${overview.setupStepsTotal} setup steps`
      : null;

  return (
    <DashboardLayout
      moduleLabel="Finance"
      title="Finance overview"
      description="Executive view of cash, revenue, and business health — no accounting jargon"
      actions={
        !overview?.setupComplete ? (
          <ButtonLink to="/finance/setup/wizard" size="sm">
            Continue setup wizard
          </ButtonLink>
        ) : (
          <ButtonLink to="/finance/health" variant="secondary" size="sm">
            Health center
          </ButtonLink>
        )
      }
    >
      {!overview?.setupComplete && setupProgress && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
          Finance setup is incomplete ({setupProgress}).{' '}
          <Link to="/finance/setup/wizard" className="font-medium text-primary-600 hover:underline">
            Finish guided setup
          </Link>{' '}
          to unlock go-live checks and close management.
        </div>
      )}

      <KpiGrid>
        <StatCard
          title="Business health"
          value={overview?.businessHealthScore ?? '—'}
          icon={<TrendingUpIcon />}
          trend={overview?.healthGrade}
        />
        <StatCard title="Cash position" value={formatMoney(overview?.cashPosition)} icon={<AccountBalanceIcon />} />
        <StatCard title="30-day cash forecast" value={formatMoney(overview?.cashForecast30Days)} />
        <StatCard title="Revenue (MTD)" value={formatMoney(overview?.revenueMtd)} />
        <StatCard title="Profit (MTD)" value={formatMoney(overview?.profitMtd)} />
        <StatCard title="Open receivables" value={formatMoney(overview?.openReceivables)} icon={<ReceiptIcon />} />
        <StatCard title="Open payables" value={formatMoney(overview?.openPayables)} icon={<PaymentsIcon />} />
        <StatCard title="Pending approvals" value={overview?.pendingApprovals ?? '—'} icon={<WarningAmberIcon />} />
      </KpiGrid>

      {overview?.healthRecommendations && overview.healthRecommendations.length > 0 && (
        <PageSection title="Recommendations" className="mt-6">
          <ul className="text-sm space-y-1 list-disc pl-5 text-slate-600">
            {overview.healthRecommendations.map((rec) => (
              <li key={rec}>{rec}</li>
            ))}
          </ul>
        </PageSection>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <PageSection title="Top issues">
          <IssuePreview issues={overview?.topIssues ?? []} />
          {(overview?.topIssues?.length ?? 0) > 0 && (
            <Link to="/finance/health" className="inline-block mt-3 text-sm text-primary-600 hover:underline">
              View all in health center →
            </Link>
          )}
        </PageSection>

        <PageSection title="Workspaces">
          <QuickActionGrid items={WORKSPACE_ACTIONS} columns={2} />
        </PageSection>
      </div>

      <p className="mt-6 text-xs text-slate-500">
        Need journal detail?{' '}
        <Link to="/finance/accounting" className="text-primary-600 hover:underline">
          Open accounting workspace
        </Link>{' '}
        (for accountants only).
      </p>
    </DashboardLayout>
  );
}
